import Head from 'next/head';
import getConfig from 'next/config';
import { NextSeo } from 'next-seo';
import type { GetServerSidePropsContext } from 'next';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { getServerSession } from 'next-auth';
import { useEffect } from 'react';
import { AxiosError } from 'axios';

import { logger } from '@dist-marketplace-frontend/services';

import { VerificationProvider } from '../../contexts/VerificationContext';
import { DefaultLayout } from '../../components/Layouts';
import { Notification } from '../../domains/Notification';
import { ResponseMakeModelType, ResponseModelFilter } from '../../types';
import { PAGE } from '../../helpers/pages';
import { getTLD } from '../../helpers/domain';
import { showroomRepository } from '../../repositories/ShowroomRepository';
import { Dealer } from '../../domains/Dealer';
import { isError, isOk } from '../../domains/Result';
import DealerPage, {
  DealerPages,
} from '../../components/DealerPage/DealerPage';
import {
  IGetAdsRequest,
  IGetAdsRequestFilter,
  IGetAdsRequestRange,
  IGetSection,
  ISearchPageAds,
  ISearchPageFilter,
  ISearchPagePaging,
} from '../../api/types/searchPageApiTypes';
import { DealerPageProvider } from '../../components/DealerPage/context/DealerPageContext';
import type { ViewType } from '../../components/DealerPage/DealerPage.typed';
import { createHiddenFilters } from '../../components/DealerPage/mappers/filters';
import {
  getMakeModelParams,
  getQueryParams,
  mapFilterAndRangeValues,
} from '../../helpers/seo/searchParams';
import { searchPageApi } from '../../api/searchPageApi';
import { DealerFilterProvider } from '../../components/DealerPage/context/DealerFilterContext';
import {
  getSeoTitle,
  getSeoDescription,
} from '../../helpers/seo/dealerShowroomSeo';
import { VERTICAL } from '../../helpers/verticals';
import { useVerticalContext } from '../../contexts/VerticalContext';
import { formatAuthorizationHeader } from '../../helpers/auth';
import { nextAuthOptions } from '../../helpers/nextAuthOptions';
import { getServerCookie } from '../../utils/CookieUtils';
import { COOKIE } from '../../helpers/constants';
import { dealerDirectoryQuickLinks } from '../../components/DealerDirectory/queries/dealerDirectoryQuickLinks.query';
import {
  QUICK_LINKS_COUNTY_VALUE,
  QUICK_LINKS_FRANCHISE_VALUE,
} from '../../components/DealerPage/DealerPage.constants';
import { showroomFilters } from '../../components/DealerPage/queries/showroomFilters';
import { asString } from '../../utils/query-parameters';
import type { ServerResponse, ServerSideProps } from '../../types/server';

interface DealerPageProps {
  domain: string;
  notification: Notification;
  dealer: Dealer;
  ads: ISearchPageAds[];
  sort: string;
  section: string;
  sectionsData: IGetSection[];
  sectionName: string;
  parentName: string | null;
  parentDisplayName: string | null;
  searchMatches: number;
  paging: ISearchPagePaging;
  viewType: ViewType;
  filtersData: ISearchPageFilter[];
  filtersMakeModel: ResponseModelFilter;
  makeModelParams: ResponseMakeModelType;
  filtersQueryParams: IGetAdsRequestFilter[];
  rangeQueryParams: IGetAdsRequestRange[];
  page: DealerPages;
  pageParam: string;
  seoDisplayName: string;
  seoCategoryDisplayName: string;
}

function Section({
  domain,
  notification,
  dealer,
  ads,
  section,
  sectionsData,
  searchMatches,
  parentDisplayName,
  sectionName,
  parentName,
  sort,
  paging,
  viewType,
  filtersData,
  filtersMakeModel,
  makeModelParams,
  filtersQueryParams,
  rangeQueryParams,
  page,
  pageParam,
  seoDisplayName,
  seoCategoryDisplayName,
}: DealerPageProps) {
  const {
    publicRuntimeConfig: { CDN_STATIC_ASSETS },
  } = getConfig();

  const { name, address, slug } = dealer;
  const { updateVertical } = useVerticalContext();

  useEffect(() => {
    updateVertical(VERTICAL.MOTOR);
  }, [updateVertical]);

  const hasAds = ads && ads.length > 0;

  const getSeoCanonicalUrl = () => {
    const formattedPageParam = pageParam ? `/${pageParam}` : '';
    return `${domain}/dealer/${slug}${formattedPageParam}`;
  };

  const formattedNotificationData = {
    totalUnread: notification.total,
    unreadHistoryChecks: notification.historyChecks,
    unreadMessages: notification.messages,
  };

  return (
    <VerificationProvider>
      <NextSeo
        title={getSeoTitle({
          page,
          name,
          seoDisplayName,
          address,
        })}
        noindex={!hasAds}
        nofollow={!hasAds}
        description={getSeoDescription({
          page,
          name,
          seoCategoryDisplayName,
          address,
        })}
        canonical={getSeoCanonicalUrl()}
      />
      <Head>
        <meta
          name="google-site-verification"
          content="GHUykIchXG6tmbMfI3F6rSG5MVDBjzJN6iCyRilt8Kw"
        />
      </Head>

      <DefaultLayout
        domain={domain}
        headerVariant="TRANSPARENT"
        notificationData={formattedNotificationData}
        useGenericLogoInHeader
      >
        <DealerPageProvider
          sort={sort}
          section={section}
          paging={paging}
          viewType={viewType}
          saved={false}
          baseUrl={domain}
          initialBreadcrumbs={[]}
          initialRecommendations={[]}
        >
          <DealerFilterProvider
            filtersData={filtersData}
            filtersMakeModelData={filtersMakeModel}
            filtersQueryParams={filtersQueryParams}
            makeModelParams={makeModelParams}
            rangeQueryParams={rangeQueryParams}
          >
            <DealerPage
              cdnUrl={CDN_STATIC_ASSETS}
              domain={domain}
              dealer={dealer}
              ads={ads}
              gtm={{}}
              dfp={{
                vertical: [],
                section: [],
                words: [],
                area: [],
                user_id: [],
              }}
              seoData={{}}
              sectionsData={sectionsData}
              searchMatches={searchMatches}
              parentDisplayName={parentDisplayName}
              sectionName={sectionName}
              parentName={parentName}
              page={page}
            />
          </DealerFilterProvider>
        </DealerPageProvider>
      </DefaultLayout>
    </VerificationProvider>
  );
}

const getPageName = (section: string) => {
  switch (section) {
    case 'contact':
      return DealerPages.contact;
    case 'reviews':
      return DealerPages.reviews;
    default:
      return DealerPages.our_stock;
  }
};

const getMakeModelFilters = async (
  section: string,
  makeModelFilter: ISearchPageFilter | undefined,
  makeModelParams: ResponseMakeModelType | undefined,
  dealerId: string | null = null,
): Promise<ResponseModelFilter> => {
  let makeModelFiltersResponse: ResponseModelFilter = [];

  if (makeModelParams && makeModelFilter && section) {
    for (const makeModelParam of makeModelParams) {
      const selectedMakeFilter =
        (makeModelFilter.values &&
          makeModelFilter.values[0].all?.find(
            (valueItem) => valueItem.displayName === makeModelParam.make,
          )) ||
        [];
      if (selectedMakeFilter && !!makeModelParam.model) {
        const allModels = await searchPageApi.getModelByMake(
          section,
          makeModelParam.make,
          { accept: 'application/json' },
          dealerId,
        );
        allModels.data &&
          !makeModelFiltersResponse.find(
            (item) => item.makeName === makeModelParam.make,
          ) &&
          makeModelFiltersResponse.push({
            makeName: makeModelParam.make,
            ...allModels.data,
          });
      }
    }
  }
  return makeModelFiltersResponse;
};

export const getServerSideProps = async (
  ctx: GetServerSidePropsContext<{ section: string[] }>,
): ServerResponse<ServerSideProps<DealerPageProps>> => {
  const {
    publicRuntimeConfig: { BASE_URL, API_LISTINGS_TIMEOUT },
  } = getConfig();

  const tld = getTLD(ctx.req);
  let domain = `${BASE_URL}${tld}`;
  try {
    /**
     * If no dealerId is found we set the statusCode to 404 and respond.
     * We also do not catch any errors here as we want to allow Next to handle.
     */
    const dealerSlug = ctx.params?.section[0];

    if (!dealerSlug) {
      return { notFound: true };
    }

    const pageParam = ctx.params?.section[1] ?? '';

    const makeParam = ctx.params?.section[2];
    const modelParam = ctx.params?.section[3];
    const makeModelFilterParam = ctx.params?.section[4];
    const makeModelPathParams: ResponseMakeModelType | undefined = makeParam
      ? [{ make: makeParam, model: modelParam ?? '' }]
      : undefined;
    const makeModelQueryParams = ctx.query.make
      ? getMakeModelParams(ctx.query.make)
      : [];
    const makeModelParams = makeModelPathParams ?? makeModelQueryParams;

    const startQueryParam = asString(ctx.query.start);

    let section = pageParam || 'all';
    const pageName = getPageName(section);
    let seoDisplayName = 'Car Dealer';
    let seoCategoryDisplayName = 'Car';

    const queryClient = new QueryClient({
      defaultOptions: {
        queries: { retry: false },
      },
    });

    const filtersData = await queryClient.fetchQuery(
      showroomFilters(section, dealerSlug),
    );

    const filterData = [...filtersData.showByDefault, ...createHiddenFilters()];

    const {
      section: sectionQueryParam,
      sort: sortQueryParam,
      make,
      ...rest
    } = ctx.query;

    const makeModelFilterQuery = makeModelFilterParam
      ? getQueryParams(filterData, makeModelFilterParam)
      : undefined;

    const {
      filterGroup: mappedFiltersQueryParams,
      rangeGroup: mappedRangesQueryParams,
    } = mapFilterAndRangeValues(
      { ...rest, ...makeModelFilterQuery },
      filterData,
      undefined,
    );

    if (pageName === DealerPages.reviews || pageName === DealerPages.contact) {
      section = 'all';
    }

    const makeModelFilter = filterData.find(
      (filter) => filter.name === 'makeModel',
    );

    const mappedGetAdsRequestParams: IGetAdsRequest = {
      ...(makeModelFilter && {
        makeModelFilters: makeModelParams,
      }),
      filters: mappedFiltersQueryParams,
      ranges: mappedRangesQueryParams,
      paging: {
        from: startQueryParam ? parseInt(startQueryParam) : 0,
        pageSize: 10,
      },
      sections: [section],
      sort: asString(sortQueryParam),
    };

    const session = await getServerSession(ctx.req, ctx.res, nextAuthOptions);

    const ddClientId = getServerCookie(COOKIE.DD_CLIENT_ID, ctx, true) as
      | string
      | undefined;

    const dealerShowroomResponse = await showroomRepository.getDealerShowroom(
      dealerSlug,
      mappedGetAdsRequestParams,
      {
        ...formatAuthorizationHeader(session?.accessToken),
        ...(ddClientId && { Cookie: `${COOKIE.DD_CLIENT_ID}=${ddClientId}` }),
      },
    );

    if (isError(dealerShowroomResponse)) {
      if (
        dealerShowroomResponse.error instanceof AxiosError &&
        dealerShowroomResponse.error.status === 404
      ) {
        throw new NotFoundError();
      }

      throw dealerShowroomResponse.error;
    }

    const dealerId = dealerShowroomResponse.ok.dealer.id;
    const dealerIdFilter = {
      name: 'dealerId',
      values: [dealerId],
    };

    const sections = await searchPageApi.getSections(
      section,
      {
        ...mappedGetAdsRequestParams,
        filters: mappedGetAdsRequestParams.filters.concat(dealerIdFilter),
      },
      API_LISTINGS_TIMEOUT,
      ddClientId
        ? { Cookie: `${COOKIE.DD_CLIENT_ID}=${ddClientId}` }
        : undefined,
    );

    const makeModelFiltersData = await getMakeModelFilters(
      section,
      makeModelFilter,
      makeModelParams,
      dealerId,
    );

    const dealerShowroomSeoResponse =
      await showroomRepository.getDealerShowroomSeo(dealerId);

    if (isOk(dealerShowroomSeoResponse)) {
      seoDisplayName = dealerShowroomSeoResponse.ok.seoDisplayName;
      seoCategoryDisplayName =
        dealerShowroomSeoResponse.ok.seoCategoryDisplayName;
    }

    const hasReviews = Boolean(dealerShowroomResponse.ok.dealer.googleReview);

    if (pageName === DealerPages.reviews && !hasReviews) {
      return {
        redirect: {
          permanent: true,
          destination: `/dealer/${dealerShowroomResponse.ok.dealer.slug}`,
        },
      };
    }

    await queryClient.prefetchQuery(
      dealerDirectoryQuickLinks(
        QUICK_LINKS_COUNTY_VALUE,
        QUICK_LINKS_FRANCHISE_VALUE,
        ddClientId
          ? { Cookie: `${COOKIE.DD_CLIENT_ID}=${ddClientId}` }
          : undefined,
      ),
    );

    return {
      props: {
        sort: asString(ctx.query.sort) ?? '',
        section,
        filtersData: filterData || [],
        filtersMakeModel: makeModelFiltersData || [],
        makeModelParams: makeModelParams,
        filtersQueryParams: mappedFiltersQueryParams,
        rangeQueryParams: mappedRangesQueryParams,
        sectionsData: sections.data.subSections || [],
        sectionName: sections.data.displayName,
        parentName: sections.data.parentName || null,
        parentDisplayName: sections.data.parentDisplayName || null,
        searchMatches: sections.data.searchMatches || 0,
        paging: dealerShowroomResponse.ok.paging,
        // TODO: get view type from cookies to match search page behaviour
        viewType: 'list',
        dealer: dealerShowroomResponse.ok.dealer,
        notification: dealerShowroomResponse.ok.notification,
        ads: dealerShowroomResponse.ok.ads,
        domain,
        page: pageName,
        pageParam,
        seoDisplayName,
        seoCategoryDisplayName,
        dehydratedState: dehydrate(queryClient),
      },
    };
  } catch (error) {
    if (error instanceof NotFoundError) {
      return {
        notFound: true,
      };
    }

    logger.error({
      route: PAGE.DEALER_DETAILS,
      error,
      message: 'Failed to get dealer data',
    });

    throw error;
  }
};

class NotFoundError extends Error {}

export default Section;
